import {
    createContext,
    memo,
    type ReactElement,
    type ReactNode,
    type RefObject,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSearchParams } from '@direct-frontend/modules-router';
import { noop, type Nullable, type ValueOf } from '@direct-frontend/stdlib';
import { loadYandexKit } from './lib/loadYandexKit';
import {
    type PreviewTokenProvider,
    type YandexKit,
    type YandexKitModalRenderer,
    type YandexKitPartialDto,
    type YandexKitRenderer,
} from './types/YandexKit';
import { getRequestContext, getSafeUserRequestContext } from '../../lib/context';
import { getWindow } from '../../lib/getWindow';
import { getPreviewUrl, setPreviewUrl, YAKIT_FORCE_SET_PREVIEW_KEY } from '../../lib/storage';
import { YandexKitStatus } from './constants';

const context = createContext<Nullable<YandexKit>>(null);
const PreviewTokenContext = createContext<PreviewTokenProvider | undefined>(undefined);
const PreviewUrlContext = createContext<Nullable<string>>(null);
const PartialsContext = createContext<YandexKitPartialDto[]>([]);

function useYandexKitInstance(): YandexKit {
    const yandexKit = useContext(context);

    if (!yandexKit) {
        throw new Error('Hook useYandexKit is called outside of YandexKitProvider');
    }

    return yandexKit;
}

export function useYandexKitRenderer(containerRef: RefObject<HTMLElement | null>): Nullable<YandexKitRenderer> {
    // Берём сырой инстанс, без обёртки partials — иначе renderer пересоздаётся при правке фрагментов
    const yandexKit = useYandexKitInstance();
    const previewUrl = useYandexKitPreviewUrl();
    const getAccessToken = useContext(PreviewTokenContext);
    const [renderer, setRenderer] = useState<Nullable<YandexKitRenderer>>(null);

    useEffect(() => {
        let isDestroyed = false;
        let rendererInstance: Nullable<YandexKitRenderer> = null;

        if (!containerRef.current) {
            setRenderer(null);

            return noop;
        }

        // при разработке с локальным превью - нужно, чтобы запросы шли через локальную проксю,
        // иначе будет cors policy ошибка т.к. бэк отдает access-control-allow-origin: * для хостов кроме yastatic.net
        // а запросы с кредами требуют ответа с конкретным origin
        const apiBaseUrl = previewUrl.includes('//localhost') ? '' : (getWindow()?.location.origin ?? '');

        const rendererOptions = {
            getPreviewToken: getAccessToken,
            previewUrl,
            apiBaseUrl,
        };

        yandexKit
            .createRenderer(containerRef.current, rendererOptions)
            .then((instance) => {
                rendererInstance = instance;

                if (isDestroyed) {
                    rendererInstance.destroy?.();
                } else {
                    setRenderer(rendererInstance);
                }
            })
            .catch(() => setRenderer(null));

        return () => {
            isDestroyed = true;
            rendererInstance?.destroy?.();
        };
    }, [containerRef, getAccessToken, previewUrl, yandexKit]);

    return renderer;
}

export function useYandexKitModalRenderer(
    containerRef: RefObject<HTMLElement | null>,
    /** Смена ключа уничтожает iframe и создаёт renderer заново (нужно для сброса локального стейта формы в превью). */
    remountKey?: string
): Nullable<YandexKitModalRenderer> {
    const yandexKit = useYandexKit();
    const getAccessToken = useContext(PreviewTokenContext);
    const [renderer, setRenderer] = useState<Nullable<YandexKitModalRenderer>>(null);

    useEffect(() => {
        let isDestroyed = false;
        let rendererInstance: Nullable<YandexKitModalRenderer> = null;

        if (!containerRef.current || typeof yandexKit.createModalRenderer !== 'function') {
            setRenderer(null);

            return noop;
        }

        yandexKit
            .createModalRenderer(containerRef.current, { getPreviewToken: getAccessToken })
            .then((instance) => {
                rendererInstance = instance;

                if (isDestroyed) {
                    rendererInstance.destroy?.();
                } else {
                    setRenderer(rendererInstance);
                }
            })
            .catch(() => setRenderer(null));

        return () => {
            isDestroyed = true;
            rendererInstance?.destroy?.();
        };
    }, [containerRef, getAccessToken, remountKey, yandexKit]);

    return renderer;
}

/**
 * Провайдер partials для getSettings / getAllSettings / denormalizeJsonSchema.
 * useYandexKit подставляет их автоматически — call-site'ы менять не нужно.
 *
 * @kind component
 */
export const YandexKitPartialsProvider = memo(function YandexKitPartialsProvider(props: {
    partials: YandexKitPartialDto[];
    children: ReactNode;
}) {
    const { partials, children } = props;

    return <PartialsContext.Provider value={partials}>{children}</PartialsContext.Provider>;
});

/**
 * Partials из ближайшего YandexKitPartialsProvider (для render → section_template_fragments).
 *
 * @kind helper
 * @returns Массив DTO фрагментов
 */
export function useYandexKitPartials(): YandexKitPartialDto[] {
    return useContext(PartialsContext);
}

export function useYandexKit(): YandexKit {
    const yandexKit = useYandexKitInstance();
    const contextPartials = useContext(PartialsContext);

    return useMemo(() => {
        if (contextPartials.length === 0) {
            return yandexKit;
        }

        // Proxy вместо spread: у class-инстанса методы на прототипе, `...yandexKit` их теряет.
        // Не делаем .bind на каждый get — иначе collectPartials и др. получают новую ссылку
        // на каждый рендер → onCodeChange меняется → CodeEditor ремаунтится и теряет фокус.
        return new Proxy(yandexKit, {
            get(target, prop) {
                if (prop === 'getSettings') {
                    return (
                        section?: Parameters<YandexKit['getSettings']>[0],
                        partials?: Parameters<YandexKit['getSettings']>[1]
                    ) => target.getSettings(section, partials ?? contextPartials);
                }

                if (prop === 'getAllSettings') {
                    return (partials?: Parameters<YandexKit['getAllSettings']>[0]) =>
                        target.getAllSettings(partials ?? contextPartials);
                }

                if (prop === 'denormalizeJsonSchema') {
                    return (
                        schema: Parameters<YandexKit['denormalizeJsonSchema']>[0],
                        partials?: Parameters<YandexKit['denormalizeJsonSchema']>[1]
                    ) => target.denormalizeJsonSchema(schema, partials ?? contextPartials);
                }

                return Reflect.get(target, prop, target);
            },
        });
    }, [yandexKit, contextPartials]);
}

export function useYandexKitPreviewUrl(): string {
    const previewUrl = useContext(PreviewUrlContext);

    if (!previewUrl) {
        throw new Error('Hook useYandexKitPreviewUrl is called outside of PreviewUrlContext');
    }

    return previewUrl;
}

interface YandexKitProviderProps {
    getAccessToken: PreviewTokenProvider;
    children: ReactElement;
    fallback: ReactElement;
}

export const YandexKitProvider = memo(function YandexKitProvider(props: YandexKitProviderProps) {
    const { children, fallback, getAccessToken } = props;

    const { isInternalYandexUser } = getSafeUserRequestContext();

    const [status, setStatus] = useState<ValueOf<typeof YandexKitStatus>>(YandexKitStatus.NOT_INITIALIZED);
    const [yandexKit, setYandexKit] = useState<Nullable<YandexKit>>(null);
    const [scriptSrc, setScriptSrc] = useState(() => {
        const forceSetPreview = getPreviewUrl();

        if (getRequestContext().env === 'development' && __IS_LOCAL__ && !forceSetPreview) {
            // когда iframe сервируется с *yastatic.net и при локальной разработке apiBaseUrl - локальный адрес yastore.yandex.ru:9000,
            // браузер блочит запросы CORS политикой (с https ресурса на loopback), поэтому достаем дефолтный превью с yastatic через проксю
            return '/preview.js';
        }

        if (getRequestContext().env === 'production' || !forceSetPreview) {
            return __PREVIEW_JS_URL__;
        }

        return forceSetPreview;
    });
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const forceSetPreview = searchParams.get(YAKIT_FORCE_SET_PREVIEW_KEY);

        if (forceSetPreview && isInternalYandexUser) {
            searchParams.delete(YAKIT_FORCE_SET_PREVIEW_KEY);
            setSearchParams(searchParams);
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-288723
            setScriptSrc(forceSetPreview);
            setPreviewUrl(forceSetPreview);
        }
    }, [isInternalYandexUser, searchParams, setSearchParams]);

    useEffect(() => {
        if (scriptSrc) {
            loadYandexKit({ src: scriptSrc })
                .then((api) => {
                    setStatus(YandexKitStatus.READY);
                    setYandexKit(api);
                })
                .catch(() => setStatus(YandexKitStatus.ERROR));
        }
    }, [scriptSrc]);

    // Слушаем изменения preview URL из дев меню
    useEffect(() => {
        const handlePreviewChanged = () => {
            const newPreviewUrl = getPreviewUrl();

            if (newPreviewUrl !== scriptSrc) {
                setScriptSrc(newPreviewUrl || __PREVIEW_JS_URL__);
                setStatus(YandexKitStatus.NOT_INITIALIZED);
                setYandexKit(null);
            }
        };

        window.addEventListener('yakit-preview-changed', handlePreviewChanged);

        return () => window.removeEventListener('yakit-preview-changed', handlePreviewChanged);
    }, [scriptSrc]);

    if (status !== YandexKitStatus.READY) {
        return fallback;
    }

    const previewUrl = scriptSrc.replace('preview.js', 'preview.html');

    return (
        <PreviewUrlContext.Provider value={previewUrl}>
            <PreviewTokenContext.Provider value={getAccessToken}>
                <context.Provider value={yandexKit}>{children}</context.Provider>
            </PreviewTokenContext.Provider>
        </PreviewUrlContext.Provider>
    );
});

export interface StaticYandexKitProviderProps {
    value: YandexKit;
    children: ReactNode;
}

/** Провайдер для Storybook / тестов: готовый экземпляр kit без загрузки скрипта. */
export const StaticYandexKitProvider = memo(function StaticYandexKitProvider(props: StaticYandexKitProviderProps) {
    const { value, children } = props;

    return <context.Provider value={value}>{children}</context.Provider>;
});
